// V2 signaling integration (9.0.0)
//
// BREAKING CHANGE for callers (calls.async.ts):
//   - emitSignalingData callback now receives Uint8Array (encrypted bytes), NOT P2pMessage JSON.
//     Callers must pass the raw bytes directly to phone.sendSignalingData (base64 encode if needed).
//   - Incoming signaling: call processSignalingData(encryptedBytes: Uint8Array) instead of
//     processSignalingMessage(parsedJson). The old processSignalingMessage is kept but internal-only.
//   - joinPhoneCall now requires a callKey parameter (Uint8Array, 256-byte shared secret from DH).

import { black, silence } from './blacksilence';
import type { ApiPhoneCallConnection, P2pParsedSdp } from './types';
import parseSdp from './parseSdp';
import type { MediaContent, P2pMessage } from './p2pMessage';
import {
  fromTelegramSource,
  IS_ECHO_CANCELLATION_SUPPORTED,
  IS_NOISE_SUPPRESSION_SUPPORTED,
} from './utils';
import type { Conference } from './buildSdp';
import { StreamType } from './secretsauce';
import { EncryptedSignalingConnection } from './encryptedConnection';
import type {
  SignalingMessage,
  InitialSetupMessage as V2InitialSetupMessage,
  NegotiateChannelsMessage as V2NegotiateChannelsMessage,
  MediaContent as V2MediaContent,
  MediaStateMessage as V2MediaStateMessage,
} from './signalingV2';
import { serializeSignalingMessage, deserializeSignalingMessage } from './signalingV2';

type P2pState = {
  connection: RTCPeerConnection;
  dataChannel: RTCDataChannel;
  emitSignalingData: (data: Uint8Array) => void;
  onUpdate: (...args: any[]) => void;
  conference?: Partial<Conference>;
  isOutgoing: boolean;
  pendingCandidates: string[];
  streams: {
    video?: MediaStream;
    audio?: MediaStream;
    presentation?: MediaStream;
    ownAudio?: MediaStream;
    ownVideo?: MediaStream;
    ownPresentation?: MediaStream;
  };
  silence: MediaStream;
  blackVideo: MediaStream;
  blackPresentation: MediaStream;
  mediaState: Omit<V2MediaStateMessage, '@type'>;
  audio: HTMLAudioElement;
  gotInitialSetup?: boolean;
  facingMode?: VideoFacingModeEnum;
  encryptedConn?: EncryptedSignalingConnection;
  nextExchangeId: number;
};

let state: P2pState | undefined;

// Buffer for signaling data that arrives before EncryptedSignalingConnection is ready
let pendingEncryptedData: Uint8Array[] = [];

const ICE_CANDIDATE_POOL_SIZE = 10;

export function getStreams() {
  return state?.streams;
}

export function hasPhoneCallConnection() {
  return Boolean(state?.connection);
}

function updateStreams() {
  state?.onUpdate({
    ...state.mediaState,
    '@type': 'updatePhoneCallMediaState',
  });
}

function getUserStream(streamType: StreamType, facing: VideoFacingModeEnum = 'user') {
  if (streamType === 'presentation') {
    return (navigator.mediaDevices as any).getDisplayMedia({
      audio: false,
      video: true,
    });
  }

  return navigator.mediaDevices.getUserMedia({
    audio: streamType === 'audio' ? {
      ...(IS_ECHO_CANCELLATION_SUPPORTED && { echoCancellation: true }),
      ...(IS_NOISE_SUPPRESSION_SUPPORTED && { noiseSuppression: true }),
    } : false,
    video: streamType === 'video' ? {
      facingMode: facing,
    } : false,
  });
}

export async function switchCameraInputP2p() {
  if (!state || !state.facingMode) {
    return;
  }

  const stream = state.streams.ownVideo;

  if (!stream) return;

  const track = stream.getTracks()[0];

  if (!track) {
    return;
  }

  const sender = state.connection.getSenders().find((l) => track.id === l.track?.id);

  if (!sender) {
    return;
  }

  state.facingMode = state.facingMode === 'environment' ? 'user' : 'environment';
  try {
    const newStream = await getUserStream('video', state.facingMode);

    await sender.replaceTrack(newStream.getTracks()[0]);
    state.streams.ownVideo = newStream;
    updateStreams();
  } catch (e) {

  }
}

export async function toggleStreamP2p(streamType: StreamType, value: boolean | undefined = undefined) {
  if (!state) return;
  const stream = streamType === 'audio' ? state.streams.ownAudio
    : (streamType === 'video' ? state.streams.ownVideo : state.streams.ownPresentation);

  if (!stream) return;
  const track = stream.getTracks()[0];

  if (!track) {
    return;
  }

  const sender = state.connection.getSenders().find((l) => track.id === l.track?.id);

  if (!sender) {
    return;
  }

  value = value === undefined ? !track.enabled : value;

  try {
    if (value && !track.enabled) {
      const newStream = await getUserStream(streamType);
      newStream.getTracks()[0].onended = () => {
        toggleStreamP2p(streamType, false);
      };
      await sender.replaceTrack(newStream.getTracks()[0]);
      if (streamType === 'audio') {
        state.streams.ownAudio = newStream;
      } else if (streamType === 'video') {
        state.streams.ownVideo = newStream;
        state.facingMode = 'user';
      } else {
        state.streams.ownPresentation = newStream;
      }
      if (streamType === 'video' || streamType === 'presentation') {
        toggleStreamP2p(streamType === 'video' ? 'presentation' : 'video', false);
      }
    } else if (!value && track.enabled) {
      track.stop();
      const newStream = streamType === 'audio' ? state.silence
        : (streamType === 'video' ? state.blackVideo : state.blackPresentation);
      if (!newStream) return;

      await sender.replaceTrack(newStream.getTracks()[0]);

      if (streamType === 'audio') {
        state.streams.ownAudio = newStream;
      } else if (streamType === 'video') {
        state.streams.ownVideo = newStream;
      } else {
        state.streams.ownPresentation = newStream;
      }
    }
    updateStreams();
    sendMediaState();
  } catch (err) {
    console.error(err)
  }
}

export async function joinPhoneCall(
  connections: ApiPhoneCallConnection[],
  emitSignalingData: (data: Uint8Array) => void,
  isOutgoing: boolean,
  shouldStartVideo: boolean,
  isP2p: boolean,
  onUpdate: (...args: any[]) => void,
  callKey: Uint8Array,
) {
  const iceServers = connections.map((connection) => (
    {
      urls: [
        connection.isTurn && `turn:${connection.ip}:${connection.port}`,
        connection.isStun && `stun:${connection.ip}:${connection.port}`,
      ].filter(Boolean) as string[],
      username: connection.username,
      credential: connection.password,
    }
  ));
  const iceTransportPolicy = isP2p ? 'all' as const : 'relay' as const;
  // eslint-disable-next-line no-console
  console.log('[CALL_ICE] RTCPeerConnection config: iceTransportPolicy=' + iceTransportPolicy
    + ' servers=' + JSON.stringify(iceServers.map((s) => ({ urls: s.urls, user: s.username })))
    + ' callKey_len=' + callKey.length + ' callKey_nonzero=' + (callKey.some((b) => b !== 0))
    + ' callKey_hex8=' + Array.from(callKey.slice(0, 8)).map((b) => b.toString(16).padStart(2, '0')).join('')
    + ' isOutgoing=' + isOutgoing);
  const conn = new RTCPeerConnection({
    iceServers,
    iceTransportPolicy,
    bundlePolicy: 'max-bundle',
    iceCandidatePoolSize: ICE_CANDIDATE_POOL_SIZE,
  });
  // Expose for debugging
  (self as any).__p2pConn = conn;

  // Create encrypted signaling connection (V2 / 9.0.0)
  const encryptedConn = new EncryptedSignalingConnection(
    callKey,
    isOutgoing,
    (data: Uint8Array) => {
      emitSignalingData(data);
    },
  );

  conn.onicecandidate = (e) => {
    if (!e.candidate) {
      // eslint-disable-next-line no-console
      console.log('[CALL_ICE] onicecandidate: end-of-candidates');
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[CALL_ICE] onicecandidate: ' + e.candidate.candidate.slice(0, 60)
      + ' hasState=' + !!state + ' hasEncConn=' + !!state?.encryptedConn);
    // Send ICE candidates via encrypted signaling
    if (!state?.encryptedConn) return;
    const candidateMsg: SignalingMessage = {
      '@type': 'Candidates',
      candidates: [{
        sdpString: e.candidate.candidate,
      }],
    };
    state.encryptedConn.sendMessage(serializeSignalingMessage(candidateMsg));
  };

  conn.onconnectionstatechange = () => {
    // eslint-disable-next-line no-console
    console.log('[CALL_ICE] connectionState=' + conn.connectionState
      + ' iceState=' + conn.iceConnectionState + ' iceGathering=' + conn.iceGatheringState);
    onUpdate({
      '@type': 'updatePhoneCallConnectionState',
      connectionState: conn.connectionState,
    });
  };

  conn.ontrack = (e) => {
    if (!state) return;

    const stream = e.streams[0];
    if (e.track.kind === 'audio') {
      state.audio.srcObject = stream;
      state.audio.play().catch();
      state.streams.audio = stream;
    } else if (e.transceiver.mid === '1') {
      state.streams.video = stream;
    } else {
      state.streams.presentation = stream;
    }

    updateStreams();
  };

  conn.oniceconnectionstatechange = async (e) => {
    switch(conn.iceConnectionState) {
      case 'disconnected':
      case 'failed':
        if (isOutgoing) {
          await createOffer(conn, {
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
            iceRestart: true,
          });
        }
      default:
        break;
    }
  }

  const slnc = silence(new AudioContext());
  const video = black({ width: 640, height: 480 });
  const screenshare = black({ width: 640, height: 480 });
  conn.addTrack(slnc.getTracks()[0], slnc);
  conn.addTrack(video.getTracks()[0], video);
  conn.addTrack(screenshare.getTracks()[0], screenshare);

  const dc = conn.createDataChannel('data', {
    id: 0,
    negotiated: true,
  });

  dc.onmessage = (e) => {
    processSignalingMessage(JSON.parse(e.data));
  };

  const audio = new Audio();

  state = {
    audio,
    connection: conn,
    emitSignalingData,
    isOutgoing,
    pendingCandidates: [],
    onUpdate,
    streams: {
      ownVideo: video,
      ownAudio: slnc,
      ownPresentation: screenshare,
    },
    mediaState: {
      lowBattery: false,
      screencastState: 'inactive',
      videoState: 'inactive',
      videoRotation: 0,
      muted: true,
    },
    blackVideo: video,
    blackPresentation: screenshare,
    silence: slnc,
    dataChannel: dc,
    encryptedConn,
    nextExchangeId: 0,
  };

  try {
    toggleStreamP2p('audio', true);
  } catch (err) {
    console.error(err)
  }

  // Flush any buffered signaling data that arrived before state was ready
  if (pendingEncryptedData.length > 0) {
    // eslint-disable-next-line no-console
    console.log('[CALL_SIG] Flushing ' + pendingEncryptedData.length + ' buffered signaling packets');
    const buffered = pendingEncryptedData;
    pendingEncryptedData = [];
    for (const data of buffered) {
      await processSignalingData(data);
    }
  }

  if (isOutgoing) {
    await createOffer(conn, {
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    })
  }
}

export function stopPhoneCall() {
  if (!state) return;

  state.streams.ownVideo?.getTracks().forEach((track) => track.stop());
  state.streams.ownPresentation?.getTracks().forEach((track) => track.stop());
  state.streams.ownAudio?.getTracks().forEach((track) => track.stop());
  state.dataChannel.close();
  state.connection.close();
  state.encryptedConn?.destroy();
  state = undefined;
  pendingEncryptedData = [];
}

function sendMediaState() {
  if (!state?.encryptedConn) return;

  state.encryptedConn.sendMessage(serializeSignalingMessage({
    '@type': 'MediaState',
    videoRotation: 0,
    muted: !state.streams.ownAudio?.getTracks()[0].enabled,
    lowBattery: false,
    videoState: state.streams.ownVideo?.getTracks()[0].enabled ? 'active' : 'inactive',
    screencastState: state.streams.ownPresentation?.getTracks()[0].enabled ? 'active' : 'inactive',
  }));
}

function filterVP8(mediaContent: MediaContent) {
  if (!state || state.isOutgoing) return mediaContent;

  const { payloadTypes } = mediaContent!;
  const idx = payloadTypes.findIndex((payloadType) => payloadType.name === 'VP8');
  const vp8PayloadType = payloadTypes[idx];
  const rtxIdx = payloadTypes.findIndex((payloadType) => Number(payloadType.parameters?.apt) === vp8PayloadType.id);
  mediaContent.payloadTypes = [payloadTypes[idx], payloadTypes[rtxIdx]];

  return mediaContent;
}

// V2: transport-only InitialSetup + separate NegotiateChannels
function sendInitialSetup(sdp: P2pParsedSdp) {
  if (!state?.encryptedConn) return;

  // eslint-disable-next-line no-console
  console.log('[CALL_SIG] sendInitialSetup: ufrag=' + sdp.ufrag + ' pwd=' + sdp.pwd?.substring(0, 10)
    + ' fingerprints=' + sdp.fingerprints?.length);
  // Transport-only InitialSetup (9.0.0 format — no media content)
  const msg: V2InitialSetupMessage = {
    '@type': 'InitialSetup',
    ufrag: sdp.ufrag,
    pwd: sdp.pwd,
    renomination: false,
    fingerprints: sdp.fingerprints.map((fp) => ({
      hash: fp.hash,
      setup: fp.setup,
      fingerprint: fp.fingerprint,
    })),
  };
  state.encryptedConn.sendMessage(serializeSignalingMessage(msg));

  // Immediately follow with NegotiateChannels (media content)
  sendNegotiateChannels(sdp);
}

function sendNegotiateChannels(sdp: P2pParsedSdp) {
  if (!state?.encryptedConn) return;

  state.nextExchangeId++;
  const contents: V2MediaContent[] = [];

  // Audio
  if (sdp.ssrc) {
    contents.push({
      type: 'audio',
      ssrc: fromTelegramSource(sdp.ssrc),
      ssrcGroups: [],
      payloadTypes: sdp.audioPayloadTypes.map((pt) => ({
        id: pt.id,
        name: pt.name,
        clockrate: pt.clockrate,
        channels: pt.channels || 0,
        feedbackTypes: (pt.feedbackTypes || []).map((ft) => ({ type: ft.type, subtype: ft.subtype || '' })),
        parameters: pt.parameters || {},
      })),
      rtpExtensions: sdp.audioExtmap,
    });
  }

  // Video
  if (sdp['ssrc-groups']?.[0]) {
    const videoPayloadTypes = filterVP8({
      ssrc: fromTelegramSource(sdp['ssrc-groups'][0].sources[0]).toString(),
      ssrcGroups: [{
        semantics: sdp['ssrc-groups'][0].semantics,
        ssrcs: sdp['ssrc-groups'][0].sources.map(fromTelegramSource),
      }],
      payloadTypes: sdp.videoPayloadTypes,
      rtpExtensions: sdp.videoExtmap,
    }).payloadTypes;

    contents.push({
      type: 'video',
      ssrc: fromTelegramSource(sdp['ssrc-groups'][0].sources[0]),
      ssrcGroups: [{
        semantics: sdp['ssrc-groups'][0].semantics || '',
        ssrcs: sdp['ssrc-groups'][0].sources.map(fromTelegramSource),
      }],
      payloadTypes: videoPayloadTypes.map((pt) => ({
        id: pt.id,
        name: pt.name,
        clockrate: pt.clockrate,
        channels: pt.channels || 0,
        feedbackTypes: (pt.feedbackTypes || []).map((ft) => ({ type: ft.type, subtype: ft.subtype || '' })),
        parameters: pt.parameters || {},
      })),
      rtpExtensions: sdp.videoExtmap,
    });
  }

  // Screencast (second video entry)
  if (sdp['ssrc-groups']?.[1]) {
    const screencastPayloadTypes = filterVP8({
      ssrc: fromTelegramSource(sdp['ssrc-groups'][1].sources[0]).toString(),
      ssrcGroups: [{
        semantics: sdp['ssrc-groups'][1].semantics,
        ssrcs: sdp['ssrc-groups'][1].sources.map(fromTelegramSource),
      }],
      payloadTypes: sdp.screencastPayloadTypes,
      rtpExtensions: sdp.screencastExtmap,
    }).payloadTypes;

    contents.push({
      type: 'video',
      ssrc: fromTelegramSource(sdp['ssrc-groups'][1].sources[0]),
      ssrcGroups: [{
        semantics: sdp['ssrc-groups'][1].semantics || '',
        ssrcs: sdp['ssrc-groups'][1].sources.map(fromTelegramSource),
      }],
      payloadTypes: screencastPayloadTypes.map((pt) => ({
        id: pt.id,
        name: pt.name,
        clockrate: pt.clockrate,
        channels: pt.channels || 0,
        feedbackTypes: (pt.feedbackTypes || []).map((ft) => ({ type: ft.type, subtype: ft.subtype || '' })),
        parameters: pt.parameters || {},
      })),
      rtpExtensions: sdp.screencastExtmap,
    });
  }

  const msg: V2NegotiateChannelsMessage = {
    '@type': 'NegotiateChannels',
    exchangeId: String(state.nextExchangeId),
    contents,
  };
  state.encryptedConn.sendMessage(serializeSignalingMessage(msg));
}

// V2 entry point: decrypt incoming encrypted signaling data, then dispatch
export async function processSignalingData(encryptedData: Uint8Array) {
  if (!state?.encryptedConn) {
    // eslint-disable-next-line no-console
    console.log('[CALL_SIG] Buffering signaling data (encryptedConn not ready), len=' + encryptedData.length
      + ' buffered=' + pendingEncryptedData.length);
    pendingEncryptedData.push(encryptedData);
    return;
  }
  let jsonStrings: string[];
  try {
    jsonStrings = await state.encryptedConn.handleIncomingPacket(encryptedData);
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error('[CALL_SIG] handleIncomingPacket FAILED: ' + e.message + ' dataLen=' + encryptedData.length);
    return;
  }
  // eslint-disable-next-line no-console
  console.log('[CALL_SIG] handleIncomingPacket returned ' + jsonStrings.length + ' messages');
  for (const json of jsonStrings) {
    const msg = deserializeSignalingMessage(json);
    if (!msg) {
      // eslint-disable-next-line no-console
      console.log('[CALL_SIG] deserialize returned undefined for: ' + json.substring(0, 200));
      continue;
    }
    // eslint-disable-next-line no-console
    console.log('[CALL_SIG] V2 message received: type=' + msg['@type'] + ' json=' + json.substring(0, 200));
    await processSignalingMessage(msg as P2pMessage);
  }
}

export async function processSignalingMessage(message: P2pMessage) {
  if (!message || !state || !state.connection) return;

  switch (message['@type']) {
    case 'MediaState': {
      state.mediaState = message;
      updateStreams();
      sendMediaState();
      break;
    }
    case 'Candidates': {
      const { pendingCandidates, gotInitialSetup } = state;
      message.candidates.forEach((candidate) => {
        pendingCandidates.push(candidate.sdpString);
      });
      // eslint-disable-next-line no-console
      console.log('[CALL_ICE] Candidates received: ' + message.candidates.length
        + ' total_pending=' + pendingCandidates.length
        + ' gotInitialSetup=' + gotInitialSetup
        + ' first=' + message.candidates[0]?.sdpString?.slice(0, 60));
      if (gotInitialSetup) {
        await commitPendingIceCandidates();
      }
      break;
    }
    case 'InitialSetup': {
      const {
        connection, isOutgoing,
      } = state;
      if (!connection) return;
      // Guard against duplicate InitialSetup (multiple active updates can trigger re-sends)
      if (state.gotInitialSetup) {
        // eslint-disable-next-line no-console
        console.log('[CALL_SDP] Ignoring duplicate InitialSetup');
        return;
      }

      // eslint-disable-next-line no-console
      console.log('[CALL_SDP] InitialSetup (transport-only V2)'
        + ' ufrag=' + message.ufrag + ' setup=' + message.fingerprints?.[0]?.setup
        + ' localDesc=' + !!connection.localDescription + ' sigState=' + connection.signalingState);

      try {

      // Set early to prevent duplicate processing on error
      state.gotInitialSetup = true;

      const sdpType = isOutgoing ? 'answer' : 'offer';

      // V2: transport-only InitialSetup (fingerprints+ufrag+pwd only, no media content).
      // Build remote SDP from a temp offer to get codec info, then set as remote offer.
      // IMPORTANT: Do NOT setLocalDescription(offer) — that makes us CONTROLLING in ICE.
      // As callee we must be CONTROLLED: setRemoteDescription(offer) → createAnswer → setLocalDescription(answer)
      const tempOffer = await connection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });
      let sdp = tempOffer.sdp!;

      // Replace ICE credentials with remote's
      sdp = sdp.replace(/a=ice-ufrag:\S+/g, 'a=ice-ufrag:' + message.ufrag);
      sdp = sdp.replace(/a=ice-pwd:\S+/g, 'a=ice-pwd:' + message.pwd);

      // Replace DTLS fingerprint and set remote role to passive (server)
      if (message.fingerprints?.[0]) {
        const fp = message.fingerprints[0];
        sdp = sdp.replace(/a=fingerprint:\S+ \S+/g, 'a=fingerprint:' + fp.hash + ' ' + fp.fingerprint);
        // Remote is DTLS server (passive), we are DTLS client (active)
        sdp = sdp.replace(/a=setup:\S+/g, 'a=setup:passive');
      }

      const rebuiltSdp = sdp;
      // eslint-disable-next-line no-console
      console.log('[CALL_SDP] Built remote offer from temp, sdp_len=' + rebuiltSdp.length
        + ' ufrag=' + message.ufrag + ' fp=' + message.fingerprints?.[0]?.fingerprint?.slice(0, 20));

      // Set remote SDP as offer (we are callee → controlled ICE role)
      // eslint-disable-next-line no-console
      console.log('[CALL_SDP] setRemoteDescription type=' + sdpType + ' sdp_len=' + rebuiltSdp.length + ' signalingState=' + connection.signalingState);
      try {
        await connection.setRemoteDescription({ sdp: rebuiltSdp, type: sdpType });
        // eslint-disable-next-line no-console
        console.log('[CALL_SDP] setRemoteDescription OK, signalingState=' + connection.signalingState);
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error('[CALL_SDP] setRemoteDescription FAILED:', e.message);
      }

      if (!isOutgoing) {
        try {
          const answer = await connection.createAnswer();
          await connection.setLocalDescription(answer);
          // eslint-disable-next-line no-console
          console.log('[CALL_SDP] createAnswer OK, sdp_len=' + answer.sdp?.length
            + ' setup=' + (answer.sdp?.match(/a=setup:(\S+)/)?.[1] || '?'));
          sendInitialSetup(parseSdp(connection.localDescription!, true) as P2pParsedSdp);
        } catch (e: any) {
          // eslint-disable-next-line no-console
          console.error('[CALL_SDP] createAnswer FAILED:', e.message);
        }
      }
      await commitPendingIceCandidates();

      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error('[CALL_SDP] InitialSetup processing FAILED:', e.message);
      }
      break;
    }
    case 'NegotiateChannels': {
      await handleNegotiateChannels(message);
      break;
    }
  }
}

async function handleNegotiateChannels(message: V2NegotiateChannelsMessage | { '@type': 'NegotiateChannels'; exchangeId: string; contents: MediaContent[] }) {
  if (!state || !state.connection) return;

  const { connection } = state;

  // NegotiateChannels is tgcalls v2's codec confirmation after InitialSetup.
  // WebRTC PeerConnection already negotiated codecs via SDP in InitialSetup,
  // so we just respond with our current local media content without touching the PC.
  const localSdp = connection.localDescription;
  if (!localSdp) return;

  const parsed = parseSdp(localSdp, true) as P2pParsedSdp;
  if (!parsed.ssrc || !parsed['ssrc-groups']) return;

  const responseContents: V2MediaContent[] = [];

  responseContents.push({
    type: 'audio',
    ssrc: fromTelegramSource(parsed.ssrc),
    ssrcGroups: [],
    payloadTypes: parsed.audioPayloadTypes.map((pt) => ({
      id: pt.id,
      name: pt.name,
      clockrate: pt.clockrate,
      channels: pt.channels || 0,
      feedbackTypes: (pt.feedbackTypes || []).map((ft) => ({ type: ft.type, subtype: ft.subtype || '' })),
      parameters: pt.parameters || {},
    })),
    rtpExtensions: parsed.audioExtmap,
  });

  if (parsed['ssrc-groups'][0]) {
    const videoPayloadTypes = filterVP8({
      ssrc: fromTelegramSource(parsed['ssrc-groups'][0].sources[0]).toString(),
      ssrcGroups: [{
        semantics: parsed['ssrc-groups'][0].semantics,
        ssrcs: parsed['ssrc-groups'][0].sources.map(fromTelegramSource),
      }],
      payloadTypes: parsed.videoPayloadTypes,
      rtpExtensions: parsed.videoExtmap,
    }).payloadTypes;

    responseContents.push({
      type: 'video',
      ssrc: fromTelegramSource(parsed['ssrc-groups'][0].sources[0]),
      ssrcGroups: [{
        semantics: parsed['ssrc-groups'][0].semantics || '',
        ssrcs: parsed['ssrc-groups'][0].sources.map(fromTelegramSource),
      }],
      payloadTypes: videoPayloadTypes.map((pt) => ({
        id: pt.id,
        name: pt.name,
        clockrate: pt.clockrate,
        channels: pt.channels || 0,
        feedbackTypes: (pt.feedbackTypes || []).map((ft) => ({ type: ft.type, subtype: ft.subtype || '' })),
        parameters: pt.parameters || {},
      })),
      rtpExtensions: parsed.videoExtmap,
    });
  }

  if (!state.encryptedConn) return;

  const responseMsg: V2NegotiateChannelsMessage = {
    '@type': 'NegotiateChannels',
    exchangeId: message.exchangeId,
    contents: responseContents,
  };
  state.encryptedConn.sendMessage(serializeSignalingMessage(responseMsg));
}

async function commitPendingIceCandidates() {
  if (!state) {
    return;
  }
  const { pendingCandidates, connection } = state;
  // eslint-disable-next-line no-console
  console.log('[CALL_ICE] commitPendingIceCandidates count=' + pendingCandidates.length
    + ' signalingState=' + connection.signalingState
    + ' iceConnectionState=' + connection.iceConnectionState
    + ' hasRemoteDesc=' + !!connection.remoteDescription);
  if (!pendingCandidates.length) {
    return;
  }
  // Cannot add ICE candidates until remote description is set
  if (!connection.remoteDescription) {
    // eslint-disable-next-line no-console
    console.log('[CALL_ICE] Deferring ICE candidates (no remote description yet)');
    return;
  }
  await Promise.all(pendingCandidates.map((c) => tryAddCandidate(connection, c)));
  state.pendingCandidates = [];
}

async function tryAddCandidate(connection: RTCPeerConnection, candidate: string) {
  try {
    await connection.addIceCandidate({
      candidate,
      sdpMLineIndex: 0,
    });
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.error('[CALL_ICE] addIceCandidate FAILED:', err.message);
  }
}

async function createOffer(conn: RTCPeerConnection, params: RTCOfferOptions) {
  const offer = await conn.createOffer(params);
  await conn.setLocalDescription(offer);
  sendInitialSetup(parseSdp(conn.localDescription!, true) as P2pParsedSdp);
}
